import * as Phaser from "phaser"
import { EventBus } from "../core/event-bus"
import { InputManager } from "../core/input-mapper"
import { AUDIO, EVENT, SCENE } from "../core/keys"
import { GAME_HEIGHT, GAME_WIDTH } from "../data/constants"
import { SfxDebug } from "../systems/sfx-debug"
import { playSfx } from "../systems/sfx-generator"
import type { EnemyType, GameSprite } from "../types"
import { type WaveDebugInspectState, getWaveRuntime } from "./wave-runtime-api"

const PANEL_W = 220
const PANEL_H = 420

export let DEBUG_GAME_SPEED = 1
const BTN_SIZE = 36
const MARGIN = 8

export class DebugScene extends Phaser.Scene {
  private fab!: Phaser.GameObjects.Container
  private panel!: Phaser.GameObjects.Container
  private isOpen = false
  private fpsText!: Phaser.GameObjects.Text

  // SFX debug
  private sfxPanel!: Phaser.GameObjects.Container
  private sfxOpen = false
  private sfxLatencyText!: Phaser.GameObjects.Text
  private sfxRows: Phaser.GameObjects.Text[] = []
  private sfxBars!: Phaser.GameObjects.Graphics
  private sfxFlash!: Phaser.GameObjects.Rectangle
  private enemiesKilledThisWave = 0
  private lastWave = -1
  private inputManager!: InputManager

  private readonly handleEnemyDefeated = (): void => {
    this.enemiesKilledThisWave++
  }

  constructor() {
    super({ key: SCENE.DEBUG })
  }

  create(): void {
    this.inputManager = new InputManager(this, {
      onDown: (action) => {
        switch (action) {
          case "DEBUG_PAUSE":
            this.debugPause()
            break
          case "DEBUG_PHYSICS":
            this.togglePhysicsDebug()
            break
          case "DEBUG_INSPECT":
            this.toggleInspect()
            break
          case "DEBUG_TOGGLE":
            this.toggle()
            break
          case "DEBUG_SFX":
            this.toggleSfxDebug()
            break
        }
      },
    })

    this.fab = this.add.container(
      GAME_WIDTH - MARGIN - BTN_SIZE / 2,
      GAME_HEIGHT - MARGIN - BTN_SIZE / 2,
    )
    this.fab.setDepth(9999)

    const fabBg = this.add.graphics()
    fabBg.fillStyle(0x111122, 0.85)
    fabBg.fillRoundedRect(-BTN_SIZE / 2, -BTN_SIZE / 2, BTN_SIZE, BTN_SIZE, 8)
    fabBg.lineStyle(1, 0xfbbf24, 0.5)
    fabBg.strokeRoundedRect(-BTN_SIZE / 2, -BTN_SIZE / 2, BTN_SIZE, BTN_SIZE, 8)
    this.fab.add(fabBg)

    const fabIcon = this.add
      .text(0, 0, "⚡", {
        fontSize: "16px",
      })
      .setOrigin(0.5)
    this.fab.add(fabIcon)

    const fabZone = this.add
      .zone(0, 0, BTN_SIZE, BTN_SIZE)
      .setInteractive({ useHandCursor: true })
    this.fab.add(fabZone)
    this.inputManager.bindPointer(fabZone, {
      pointerdown: () => this.toggle(),
    })

    const panelX = GAME_WIDTH - MARGIN - PANEL_W
    const panelY = GAME_HEIGHT - MARGIN - BTN_SIZE - 6 - PANEL_H
    this.panel = this.add.container(panelX, panelY)
    this.panel.setDepth(9998)
    this.panel.setVisible(false)

    const panelBg = this.add.graphics()
    panelBg.fillStyle(0x0c0a15, 0.95)
    panelBg.fillRoundedRect(0, 0, PANEL_W, PANEL_H, 10)
    panelBg.lineStyle(1, 0x333344, 0.8)
    panelBg.strokeRoundedRect(0, 0, PANEL_W, PANEL_H, 10)
    this.panel.add(panelBg)

    this.panel.add(
      this.add.text(12, 10, "DEV TOOLS", {
        fontSize: "11px",
        fontFamily: "monospace",
        fontStyle: "900",
        color: "#fbbf24",
        letterSpacing: 2,
      }),
    )

    this.fpsText = this.add.text(12, 32, "FPS: --", {
      fontSize: "11px",
      fontFamily: "monospace",
      color: "#22c55e",
    })
    this.panel.add(this.fpsText)

    let btnY = 55

    btnY = this.addButton(btnY, "Pause (F9)", () => this.debugPause())
    btnY = this.addButton(btnY, "Speed 0.25x", () => this.setGameSpeed(0.25))
    btnY = this.addButton(btnY, "Speed 1x", () => this.setGameSpeed(1))
    btnY = this.addButton(btnY, "Speed 2x", () => this.setGameSpeed(2))
    btnY = this.addButton(btnY, "Infinite Time", () =>
      this.toggleInfiniteTime(),
    )
    btnY = this.addButton(btnY, "Spawn Enemy...", () =>
      this.showEnemySpawnMenu(),
    )
    btnY = this.addButton(btnY, "Kill All", () => this.killAllEnemies())
    btnY = this.addButton(btnY, "Trigger Sector Clear", () =>
      this.triggerSectorClear(),
    )
    btnY = this.addButton(btnY, "Physics (F8)", () => this.togglePhysicsDebug())
    btnY = this.addButton(btnY, "Inspect (F11)", () => this.toggleInspect())
    btnY = this.addButton(btnY, "Skip Wave...", () => this.skipToWave())
    btnY = this.addButton(btnY, "Jump Scene...", () => this.showSceneJumpMenu())
    btnY = this.addButton(btnY, "Log State", () => this.logGameState())
    btnY = this.addButton(btnY, "SFX Debug (F7)", () => this.toggleSfxDebug())

    this.buildSfxDebugPanel()

    EventBus.on(EVENT.ENEMY_DEFEATED, this.handleEnemyDefeated)

    this.events.once("shutdown", () => {
      this.inputManager.destroy()
      EventBus.off(EVENT.ENEMY_DEFEATED, this.handleEnemyDefeated)
    })
  }

  update(): void {
    const fps = Math.round(this.game.loop.actualFps)
    this.fpsText.setText(`FPS: ${fps}`)

    if (this.physicsDebugOn) {
      const scenes = this.game.scene.getScenes(true)
      for (const s of scenes) {
        if (!s.physics?.world) continue
        if (!s.physics.world.drawDebug) {
          if (!s.physics.world.debugGraphic)
            s.physics.world.createDebugGraphic()
          s.physics.world.drawDebug = true
        }
      }
    }

    const waveRuntime = getWaveRuntime(this)
    const currentWave = waveRuntime?.getGameData()?.wave ?? -1
    if (currentWave !== this.lastWave) {
      this.lastWave = currentWave
      this.enemiesKilledThisWave = 0
    }

    if (this.sfxOpen) this.updateSfxDebug()
  }

  private toggle(): void {
    this.isOpen = !this.isOpen
    this.panel.setVisible(this.isOpen)
  }

  private addButton(y: number, label: string, onClick: () => void): number {
    const btnW = PANEL_W - 24
    const btnH = 24
    const x = 12

    const bg = this.add.graphics()
    bg.fillStyle(0x1a1a2e, 1)
    bg.fillRoundedRect(x, y, btnW, btnH, 4)
    this.panel.add(bg)

    this.panel.add(
      this.add
        .text(x + btnW / 2, y + btnH / 2, label, {
          fontSize: "10px",
          fontFamily: "monospace",
          color: "#aaa",
        })
        .setOrigin(0.5),
    )

    const zone = this.add
      .zone(x + btnW / 2, y + btnH / 2, btnW, btnH)
      .setInteractive({ useHandCursor: true })
    this.panel.add(zone)
    this.inputManager.bindPointer(zone, {
      pointerover: () =>
        bg.clear().fillStyle(0x2a2a4e, 1).fillRoundedRect(x, y, btnW, btnH, 4),
      pointerout: () =>
        bg.clear().fillStyle(0x1a1a2e, 1).fillRoundedRect(x, y, btnW, btnH, 4),
      pointerdown: onClick,
    })

    return y + btnH + 4
  }

  // ─────────────────────────────────────────────────────────────
  //  SFX DEBUG PANEL
  // ─────────────────────────────────────────────────────────────

  private toggleSfxDebug(): void {
    this.sfxOpen = !this.sfxOpen
    this.sfxPanel.setVisible(this.sfxOpen)
    if (this.sfxOpen) {
      SfxDebug.enable()
    } else {
      SfxDebug.disable()
    }
  }

  private buildSfxDebugPanel(): void {
    const px = 4
    const py = GAME_HEIGHT - 195
    const pw = GAME_WIDTH - 8
    const ph = 191

    this.sfxPanel = this.add.container(0, 0).setDepth(9997).setVisible(false)

    // Background
    const bg = this.add.graphics()
    bg.fillStyle(0x000000, 0.92)
    bg.fillRoundedRect(px, py, pw, ph, 8)
    bg.lineStyle(1, 0x00ff88, 0.4)
    bg.strokeRoundedRect(px, py, pw, ph, 8)
    this.sfxPanel.add(bg)

    // Title
    this.sfxPanel.add(
      this.add.text(px + 8, py + 4, "SFX LATENCY DEBUG  (F7 close)", {
        fontSize: "8px",
        fontFamily: "monospace",
        fontStyle: "bold",
        color: "#00ff88",
      }),
    )

    // AudioContext info (updated per-frame)
    this.sfxLatencyText = this.add.text(px + 220, py + 4, "", {
      fontSize: "8px",
      fontFamily: "monospace",
      color: "#ff8800",
    })
    this.sfxPanel.add(this.sfxLatencyText)

    // Column headers
    const hy = py + 18
    const cols = [
      { x: 8, label: "#" },
      { x: 28, label: "KEY" },
      { x: 118, label: "JS pipe" },
      { x: 170, label: "tone.js" },
      { x: 222, label: "JS TOTAL" },
      { x: 290, label: "+HW buf" },
      { x: 355, label: "PERCEIVED" },
      { x: 440, label: "TIMELINE" },
    ]
    for (const c of cols) {
      this.sfxPanel.add(
        this.add.text(px + c.x, hy, c.label, {
          fontSize: "7px",
          fontFamily: "monospace",
          color: "#666666",
        }),
      )
    }

    // Pre-create 8 text rows
    this.sfxRows = []
    for (let i = 0; i < 8; i++) {
      const t = this.add.text(px + 8, hy + 14 + i * 14, "", {
        fontSize: "8px",
        fontFamily: "monospace",
        color: "#cccccc",
      })
      this.sfxPanel.add(t)
      this.sfxRows.push(t)
    }

    // Bar graph
    this.sfxBars = this.add.graphics()
    this.sfxPanel.add(this.sfxBars)

    // Flash indicator
    this.sfxFlash = this.add.rectangle(px + pw - 20, py + 8, 12, 12, 0x333333)
    this.sfxPanel.add(this.sfxFlash)

    // Test buttons
    const btnRow = py + ph - 22
    const buttons = [
      {
        label: "UI PRESS",
        key: AUDIO.SFX_UI_PRESS,
        x: px + 10,
        color: 0xff4444,
      },
      { label: "UI BEEP", key: AUDIO.SFX_UI_BEEP, x: px + 90, color: 0xff8800 },
      {
        label: "UI NAV",
        key: AUDIO.SFX_UI_NAVIGATE,
        x: px + 165,
        color: 0xffff00,
      },
      { label: "BARK", key: AUDIO.BARK, x: px + 235, color: 0x00ff88 },
      { label: "LASER", key: AUDIO.LASER, x: px + 300, color: 0x00ccff },
      { label: "BUMP", key: AUDIO.BUMPER, x: px + 365, color: 0xcc44ff },
      { label: "EXPLODE", key: AUDIO.EXPLOSION, x: px + 430, color: 0xff2200 },
      { label: "PICKUP", key: AUDIO.PICKUP, x: px + 510, color: 0x44ff44 },
    ]
    for (const b of buttons) {
      const bw = 65
      const tbg = this.add.graphics()
      tbg.fillStyle(b.color, 0.7)
      tbg.fillRoundedRect(b.x, btnRow, bw, 16, 4)
      this.sfxPanel.add(tbg)

      this.sfxPanel.add(
        this.add
          .text(b.x + bw / 2, btnRow + 8, b.label, {
            fontSize: "7px",
            fontFamily: "monospace",
            fontStyle: "bold",
            color: "#000000",
          })
          .setOrigin(0.5),
      )

      const zone = this.add
        .zone(b.x + bw / 2, btnRow + 8, bw, 16)
        .setInteractive({ useHandCursor: true })
        .setDepth(9999)
      this.sfxPanel.add(zone)
      this.inputManager.bindPointer(zone, {
        pointerdown: () => {
          SfxDebug.markInputDown(b.key)
          playSfx(b.key)
        },
      })
    }
  }

  private updateSfxDebug(): void {
    // AudioContext info
    const hw = SfxDebug.getAudioLatencyInfo()
    this.sfxLatencyText.setText(
      `AudioCtx: ${hw.state}  base: ${hw.baseLatency.toFixed(1)}ms  output: ${hw.outputLatency.toFixed(1)}ms  ${hw.sampleRate}Hz`,
    )

    const history = SfxDebug.getHistory()
    const px = 4
    const py = GAME_HEIGHT - 195
    const hy = py + 18
    const hwLatency = hw.baseLatency + hw.outputLatency

    this.sfxBars.clear()

    for (let i = 0; i < this.sfxRows.length; i++) {
      const hi = history.length - this.sfxRows.length + i
      if (hi < 0 || hi >= history.length) {
        this.sfxRows[i].setText("")
        continue
      }

      const e = history[hi]
      const d = (v: number) => v.toFixed(1).padStart(6)

      const jsPipe = (e.triggerCall ?? 0) - (e.inputDown ?? 0)
      const toneTime = (e.triggerDone ?? 0) - (e.triggerCall ?? 0)
      const jsTotal = e.totalMs
      const perceived = jsTotal + hwLatency

      const color =
        perceived < 20 ? "#00ff88" : perceived < 40 ? "#ffff00" : "#ff4444"
      const keyStr = e.key.length > 12 ? e.key.slice(0, 12) : e.key.padEnd(12)

      this.sfxRows[i].setText(
        `${String(e.id).padStart(3)} ${keyStr} ${d(jsPipe)}ms ${d(toneTime)}ms ${d(jsTotal)}ms  +${hwLatency.toFixed(1).padStart(5)}ms  ${d(perceived)}ms`,
      )
      this.sfxRows[i].setColor(color)

      // Timeline bars
      const rowY = hy + 14 + i * 14
      const barX = px + 440
      const scale = 140 / 60

      const jsW = Math.max(1, jsPipe * scale)
      this.sfxBars.fillStyle(0x00ccff, 0.8)
      this.sfxBars.fillRect(barX, rowY + 2, jsW, 10)

      const toneW = Math.max(1, toneTime * scale)
      this.sfxBars.fillStyle(0xffff00, 0.8)
      this.sfxBars.fillRect(barX + jsW, rowY + 2, toneW, 10)

      const hwW = Math.max(1, hwLatency * scale)
      this.sfxBars.fillStyle(0xff4444, 0.6)
      this.sfxBars.fillRect(barX + jsW + toneW, rowY + 2, hwW, 10)
    }

    // Flash
    const latest = SfxDebug.getLatest()
    this.sfxFlash.setFillStyle(
      latest && performance.now() - latest.triggerDone < 100
        ? 0x00ff88
        : 0x333333,
    )
  }

  // ─────────────────────────────────────────────────────────────
  //  EXISTING DEBUG TOOLS
  // ─────────────────────────────────────────────────────────────

  private logGameState(): void {
    const waveRuntime = getWaveRuntime(this)
    if (waveRuntime) {
      const gd = waveRuntime.getGameData()
      console.log(
        `%c[BTTF] Wave ${gd.wave} | Score: ${gd.score} | HP: ${gd.player?.hp}/${gd.player?.maxHp} | Lvl: ${gd.player?.level}`,
        "color: #fbbf24",
      )
    } else {
      console.log("%c[BTTF] No active game", "color: #ef4444")
    }
  }

  private skipToWave(): void {
    const input = prompt("Wave (1-20):")
    if (!input) return
    const wave = Number.parseInt(input, 10)
    if (Number.isNaN(wave) || wave < 1 || wave > 20) return
    const waveScene = this.scene.get(SCENE.WAVE)
    if (waveScene) {
      this.scene.stop(SCENE.WAVE)
      this.scene.start(SCENE.WAVE, { wave })
    }
  }

  private infiniteTimeEnabled = false

  private toggleInfiniteTime(): void {
    this.infiniteTimeEnabled = !this.infiniteTimeEnabled
    const waveRuntime = getWaveRuntime(this)
    if (waveRuntime) {
      waveRuntime.setInfiniteTime(this.infiniteTimeEnabled)
      console.log(
        `%c[BTTF] Infinite Time: ${this.infiniteTimeEnabled ? "ON" : "OFF"}`,
        "color: #00ff88",
      )
    }
  }

  private killAllEnemies(): void {
    const waveRuntime = getWaveRuntime(this)
    if (waveRuntime) {
      waveRuntime.killAllEnemies()
      console.log("%c[BTTF] All enemies killed", "color: #ff4444")
    }
  }

  private triggerSectorClear(): void {
    const waveRuntime = getWaveRuntime(this)
    if (waveRuntime) {
      waveRuntime.triggerSectorClear()
      console.log("%c[BTTF] Sector clear triggered", "color: #00ff88")
    }
  }

  private showEnemySpawnMenu(): void {
    const enemyTypes = [
      "orbDrone",
      "harvester",
      "router",
      "spikeMine",
      "healer",
      "kamikaze",
      "gunPod",
      "bioPod",
      "chainDrifter",
      "neonMoai",
      "dwarf",
      "bomber",
      "humanoid",
      "snake",
      "brain",
    ]
    const choices = enemyTypes.map((t) => `Spawn ${t}`).join("\n")
    const input = prompt(`Enter enemy type:\n${choices}`)
    if (!input) return

    const match = enemyTypes.find(
      (t) => t.toLowerCase() === input.toLowerCase(),
    )
    if (!match) {
      console.log(`%c[BTTF] Unknown enemy type: ${input}`, "color: #ff4444")
      return
    }

    const waveRuntime = getWaveRuntime(this)
    if (waveRuntime) {
      const x = 200 + Math.random() * 400
      const y = 100 + Math.random() * 400
      waveRuntime.spawnEnemyAtPoint(match as EnemyType, x, y)
      console.log(`%c[BTTF] Spawned ${match}`, "color: #00ff88")
    }
  }

  private showSceneJumpMenu(): void {
    const scenes = [
      "Boot",
      "Title",
      "Menu",
      "Story",
      "Tutorial",
      "CharacterSelect",
      "Wave",
      "Shop",
      "GameOver",
      "Options",
      "Pause",
    ]
    const input = prompt(`Enter scene name:\n${scenes.join(", ")}`)
    if (!input) return

    const match = scenes.find((s) => s.toLowerCase() === input.toLowerCase())
    if (!match) {
      console.log(`%c[BTTF] Unknown scene: ${input}`, "color: #ff4444")
      return
    }

    const targetScene = SCENE[match as keyof typeof SCENE]
    if (!targetScene) {
      console.log(`%c[BTTF] Scene not found: ${match}`, "color: #ff4444")
      return
    }

    this.scene.stop(SCENE.WAVE)
    this.scene.stop(SCENE.HUD)
    this.scene.start(targetScene)
    console.log(`%c[BTTF] Jumped to ${match}`, "color: #00ff88")
  }

  private physicsDebugOn = false

  private togglePhysicsDebug(): void {
    this.physicsDebugOn = !this.physicsDebugOn
    const scenes = this.game.scene.getScenes(true)
    for (const s of scenes) {
      if (!s.physics?.world) continue
      const world = s.physics.world
      if (this.physicsDebugOn) {
        if (!world.debugGraphic) world.createDebugGraphic()
        world.drawDebug = true
      } else {
        world.drawDebug = false
        world.debugGraphic?.clear()
      }
    }
  }

  private debugPaused = false

  private debugPause(): void {
    this.debugPaused = !this.debugPaused
    const waveScene = this.scene.get(SCENE.WAVE)
    const waveRuntime = getWaveRuntime(this)
    if (!waveScene || !waveRuntime) return

    if (this.debugPaused) {
      waveScene.physics.world.pause()
      waveScene.time.paused = true
      waveScene.tweens.pauseAll()
      waveRuntime.setDebugFrozen(true)
    } else {
      waveScene.physics.world.resume()
      waveScene.time.paused = false
      waveScene.tweens.resumeAll()
      waveRuntime.setDebugFrozen(false)
    }
  }

  private setGameSpeed(scale: number): void {
    DEBUG_GAME_SPEED = scale
    const scenes = this.game.scene.getScenes(true)
    for (const s of scenes) {
      if (s.scene.key === SCENE.DEBUG) continue
      s.tweens.timeScale = scale
      if (s.physics?.world) {
        s.physics.world.timeScale = 1 / scale
      }
    }
  }

  private inspectMode = false
  private inspectTooltip: Phaser.GameObjects.Container | null = null
  private inspectHighlight: Phaser.GameObjects.Graphics | null = null

  private toggleInspect(): void {
    this.inspectMode = !this.inspectMode
    if (this.inspectMode) {
      this.inputManager.bindScenePointer(
        "pointerdown",
        this.onInspectClick.bind(this),
      )
    } else {
      this.clearInspectTooltip()
    }
  }

  private onInspectClick(pointer: Phaser.Input.Pointer): void {
    if (
      this.isOpen &&
      pointer.x > GAME_WIDTH - PANEL_W - MARGIN &&
      pointer.y > GAME_HEIGHT - PANEL_H - BTN_SIZE - 20
    )
      return

    const waveRuntime = getWaveRuntime(this)
    if (!waveRuntime) return

    const found = this.findEntityAt(
      waveRuntime.getDebugInspectState(),
      pointer.x,
      pointer.y,
    )
    if (found) {
      this.showInspectTooltip(
        found.label,
        found.lines,
        found.x,
        found.y,
        found.color,
      )
    } else {
      this.clearInspectTooltip()
    }
  }

  private findClosestInGroup(
    group: Phaser.Physics.Arcade.Group | undefined,
    x: number,
    y: number,
    threshold: number,
  ): GameSprite | null {
    if (!group) return null
    let best: GameSprite | null = null
    let bestDist = threshold
    for (const child of group.children) {
      const candidate = child as GameSprite
      if (!candidate.active) continue
      const d = Phaser.Math.Distance.Between(x, y, candidate.x, candidate.y)
      if (d < bestDist) {
        best = candidate
        bestDist = d
      }
    }
    return best
  }

  private findEntityAt(
    ws: WaveDebugInspectState,
    x: number,
    y: number,
  ): {
    label: string
    lines: string[]
    x: number
    y: number
    color: number
  } | null {
    const threshold = 50

    const dog = ws.dogSprite
    if (
      dog?.active &&
      Phaser.Math.Distance.Between(x, y, dog.x, dog.y) < threshold
    ) {
      const p = ws.gameData?.player
      return {
        label: "PLAYER",
        x: dog.x,
        y: dog.y,
        color: 0x22c55e,
        lines: p
          ? [
              `HP: ${p.hp}/${p.maxHp}`,
              `Lvl: ${p.level}  EXP: ${p.exp}/${p.expToLevel}`,
            ]
          : ["(no data)"],
      }
    }

    const enemy = this.findClosestInGroup(ws.enemyGroup, x, y, threshold)
    if (enemy) {
      const ed = enemy.entityData
      if (ed?.kind === "enemy") {
        return {
          label: `ENEMY: ${ed.type}`,
          x: enemy.x,
          y: enemy.y,
          color: 0xef4444,
          lines: [
            `HP: ${ed.hp.toFixed(0)}`,
            `DMG: ${ed.damage.toFixed(0)}`,
            `Points: ${ed.points}`,
          ],
        }
      }
    }

    const proj = this.findClosestInGroup(ws.projectileGroup, x, y, threshold)
    if (proj) {
      const pd = proj.entityData
      if (pd?.kind === "projectile") {
        return {
          label: "PROJECTILE",
          x: proj.x,
          y: proj.y,
          color: 0x60a5fa,
          lines: [`DMG: ${pd.damage.toFixed(1)}`, `Pierce: ${pd.pierce}`],
        }
      }
    }

    const coin = this.findClosestInGroup(ws.expCoinGroup, x, y, threshold)
    if (coin) {
      const cd = coin.entityData
      if (cd?.kind === "coin") {
        return {
          label: "COIN",
          x: coin.x,
          y: coin.y,
          color: 0xfbbf24,
          lines: [`Value: ${cd.value}`, `Size: ${cd.size}`],
        }
      }
    }

    return null
  }

  private showInspectTooltip(
    label: string,
    lines: string[],
    x: number,
    y: number,
    color: number,
  ): void {
    this.clearInspectTooltip()

    const padding = 8
    const lineH = 12
    const headerH = 16
    const w = 160
    const h = headerH + lines.length * lineH + padding * 2

    let tx = x + 20
    let ty = y - h / 2
    if (tx + w > GAME_WIDTH) tx = x - w - 20
    if (ty < 0) ty = 4
    if (ty + h > GAME_HEIGHT) ty = GAME_HEIGHT - h - 4

    this.inspectTooltip = this.add.container(tx, ty).setDepth(10001)

    const bg = this.add.graphics()
    bg.fillStyle(0x0c0a15, 0.95)
    bg.fillRoundedRect(0, 0, w, h, 6)
    bg.lineStyle(2, color, 0.8)
    bg.strokeRoundedRect(0, 0, w, h, 6)
    this.inspectTooltip.add(bg)

    this.inspectTooltip.add(
      this.add.text(padding, padding, label, {
        fontSize: "10px",
        fontFamily: "monospace",
        fontStyle: "900",
        color: `#${color.toString(16).padStart(6, "0")}`,
      }),
    )

    lines.forEach((line, i) => {
      this.inspectTooltip?.add(
        this.add.text(padding, headerH + padding + i * lineH, line, {
          fontSize: "9px",
          fontFamily: "monospace",
          color: "#aaa",
        }),
      )
    })

    this.inspectHighlight = this.add.graphics().setDepth(10000)
    this.inspectHighlight.lineStyle(2, color, 0.8)
    this.inspectHighlight.strokeCircle(x, y, 20)
  }

  private clearInspectTooltip(): void {
    this.inspectTooltip?.destroy(true)
    this.inspectTooltip = null
    this.inspectHighlight?.destroy()
    this.inspectHighlight = null
  }
}
